import { Descope } from '@descope/react-sdk/flows';
import React, { useState } from 'react';
import { getOrCreateClientSessionId } from '@shared/utils/clientSessionId';
import { withClientSessionId } from '@shared/utils/descopeActionPayload';
import CollectEmailScreen from './CollectEmailScreen';
import {
  MOBILE_CONSUMER_PROMOTION_SET_EMAIL_INTERACTION_IDS,
  MobileConsumerPromotionSetEmailScreenNames,
} from './screenMapping';
import OTPVerificationScreen from '../universal-login/screens/OTPVerificationScreen';

export const MOBILE_CONSUMER_PROMOTION_SET_EMAIL_FLOW_ID =
  'mobile-consumer-promotion-set-email';

type DescopeNext = (
  interactionId: string,
  payload?: Record<string, unknown>
) => Promise<unknown>;
type RawDescopeNext = (
  interactionId: string,
  payload: Record<string, unknown>
) => Promise<unknown>;

type FlowForm = Record<string, unknown>;
type DescopeContext = {
  error?: unknown;
  form?: FlowForm;
  [key: string]: unknown;
};
type FlowState = DescopeContext & {
  next?: DescopeNext;
  screenName?: string;
};

const getDescopeRedirectUrl = (): string => {
  const url = new URL(window.location.href);
  url.hash = '';
  return url.toString();
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object';

const getErrorText = (error: unknown): string | undefined => {
  if (!isRecord(error)) {
    return undefined;
  }

  return typeof error.text === 'string' ? error.text : undefined;
};

const getErrorDetail = (error: unknown): unknown =>
  isRecord(error) && 'detail' in error ? error.detail : error;

const resetErrorState = (
  setState: React.Dispatch<React.SetStateAction<FlowState>>
) =>
  setState(prevState => ({
    ...prevState,
    error: {},
  }));

const getEmailFromForm = (form: FlowForm): string | undefined => {
  const email = form.email;

  return typeof email === 'string' ? email : undefined;
};

const MobileConsumerPromotionSetEmailFlow: React.FC = () => {
  const [state, setState] = useState<FlowState>({ error: {} });
  const [form, setForm] = useState<FlowForm>({});
  const [descopeRedirectUrl] = useState(getDescopeRedirectUrl);
  const [clientSessionId] = useState(getOrCreateClientSessionId);
  const flowForm = {
    ...(state?.form ?? {}),
    ...form,
  };

  return (
    <Descope
      flowId={MOBILE_CONSUMER_PROMOTION_SET_EMAIL_FLOW_ID}
      styleId="linktree"
      theme="light"
      redirectUrl={descopeRedirectUrl}
      externalRequestId={clientSessionId}
      form={withClientSessionId(flowForm)}
      onScreenUpdate={(
        screenName: string,
        context: DescopeContext,
        next: RawDescopeNext
      ) => {
        const nextWithClientSessionId: DescopeNext = (interactionId, payload) =>
          next(interactionId, withClientSessionId(payload ?? {}));

        setState(prevState => ({
          ...prevState,
          ...context,
          next: nextWithClientSessionId,
          screenName,
          error: context?.error || {},
        }));

        const customScreenNames = [
          MobileConsumerPromotionSetEmailScreenNames.COLLECT_EMAIL,
          MobileConsumerPromotionSetEmailScreenNames.VERIFY_OTP,
        ];

        return customScreenNames.includes(
          screenName as MobileConsumerPromotionSetEmailScreenNames
        );
      }}
      onError={(error: unknown) => {
        setState(prevState => ({
          ...prevState,
          error: getErrorDetail(error),
        }));
      }}
    >
      {state?.screenName ===
        MobileConsumerPromotionSetEmailScreenNames.COLLECT_EMAIL && (
        <CollectEmailScreen
          initialEmail={getEmailFromForm(flowForm)}
          onEmailChange={data =>
            setForm(prevForm => ({ ...prevForm, ...data }))
          }
          onEmailSubmit={async (email: string) => {
            const nextForm = { ...flowForm, email };
            setForm(nextForm);

            if (state.next) {
              await state.next(
                MOBILE_CONSUMER_PROMOTION_SET_EMAIL_INTERACTION_IDS.SUBMIT_EMAIL,
                nextForm
              );
            }
          }}
          onClickCancel={async () => {
            if (state.next) {
              await state.next(
                MOBILE_CONSUMER_PROMOTION_SET_EMAIL_INTERACTION_IDS.CANCEL_COLLECT_EMAIL,
                flowForm
              );
            }
          }}
          errorText={getErrorText(state.error)}
          resetError={() => resetErrorState(setState)}
        />
      )}
      {state?.screenName ===
        MobileConsumerPromotionSetEmailScreenNames.VERIFY_OTP && (
        <OTPVerificationScreen
          authFlow="login"
          otpChannel="email"
          onFormUpdate={data => setForm(prevForm => ({ ...prevForm, ...data }))}
          onOTPVerificationClick={async (code: string) => {
            if (state.next) {
              await state.next(
                MOBILE_CONSUMER_PROMOTION_SET_EMAIL_INTERACTION_IDS.SUBMIT_CODE,
                {
                  ...flowForm,
                  code,
                }
              );
            }
          }}
          onClickResend={async () => {
            if (state.next) {
              await state.next(
                MOBILE_CONSUMER_PROMOTION_SET_EMAIL_INTERACTION_IDS.RESEND,
                flowForm
              );
            }
          }}
          onClickUseDifferentEmail={async () => {
            if (state.next) {
              await state.next(
                MOBILE_CONSUMER_PROMOTION_SET_EMAIL_INTERACTION_IDS.USE_DIFFERENT_EMAIL,
                flowForm
              );
            }
          }}
          onClickCancel={async () => {
            if (state.next) {
              await state.next(
                MOBILE_CONSUMER_PROMOTION_SET_EMAIL_INTERACTION_IDS.CANCEL_VERIFY_OTP,
                flowForm
              );
            }
          }}
          errorText={getErrorText(state.error)}
          context={{ ...state, form: flowForm }}
          resetError={() => resetErrorState(setState)}
        />
      )}
    </Descope>
  );
};

export default MobileConsumerPromotionSetEmailFlow;
